import React, { useState, useRef, useEffect, use } from 'react';
import { AdsCtaButton } from '@arm/arm-design-system-react';
import { Text, RichText } from '@sitecore-jss/sitecore-jss-react';
import './FeatureAdvancedVideo.css';

import { renderCtaLink } from '../FeatureComponents/FullWidthRichText/fieldsRenderer';
import { getTrackingAttributes } from '../FeatureComponents/SharedUtils/utils';
import { useInvertedColor, useTheme } from '../FeatureComponents/SharedUtils/hooks';

const FeatureAdvancedVideo = ({ fields }) => {
  const [showVideo, setShowVideo] = useState(fields?.autoplay?.value);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  const [videoDimensions, setVideoDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [introVideoPlayed, setIntroVideoPlayed] = useState(false);
  const videoRef = useRef(null);
  const videoWrapperRef = useRef(null);

  const { isLight } = useTheme();
  const isInverted = useInvertedColor(fields);
  const trackingAttributes = getTrackingAttributes(fields);

  let themeMode = 'dark';

  // To apply dark or light theme
  const BackgroundColourDark = fields?.BackgroundColourDark?.fields['value'].value;
  const BackgroundColourLight = fields?.BackgroundColourLight?.fields['value'].value;

  let modifiedBackgroundColour = BackgroundColourDark ? `u-${BackgroundColourDark}` : '';

  if (isLight) {
    modifiedBackgroundColour = BackgroundColourLight ? `u-${BackgroundColourLight}` : '';
    themeMode = 'light';
  }

  // settings
  var paddingTop = fields?.paddingTop?.value ? fields?.paddingTop?.value.replace('px', '') : 90;
  var paddingBottom = fields?.paddingBottom?.value
    ? fields?.paddingBottom?.value.replace('px', '')
    : 45;
  var height = fields?.height?.value ? fields?.height?.value.replace('px', '') : 600;
  var videoWrapperHeight = height - paddingTop - paddingBottom;

  if (fields?.isFullWidth?.value) {
    paddingTop = 0;
    paddingBottom = 0;
    height = '100%';
    videoWrapperHeight = '100%';
  }

  // Preload the video when the component mounts
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.preload = 'auto';
      videoRef.current.muted = true;
      videoRef.current.load();
    }
  }, []);

  // Get video dimensions when metadata is loaded
  const handleMetadataLoaded = () => {
    if (videoRef.current) {
      const { videoWidth, videoHeight } = videoRef.current;
      setVideoDimensions({ width: videoWidth, height: videoHeight });
    }
  };

  // Play video handler
  const handlePlayVideo = () => {
    setShowVideo(true);
    if (videoRef.current) {
      videoRef.current.hidden = false;
      videoRef.current.play().catch((error) => {
        console.log('Play failed:', error);
      });
    }
  };

  // Stop video handler
  const handleStop = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      videoRef.current.hidden = true;
    }
    exitFullscreen();
    setShowVideo(false);
  };

  // Toggle mute/unmute
  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef?.current.muted);
    }
  };

  // Toggle fullscreen
  const handleFullscreen = () => {
    if (!isFullscreen) {
      enterFullscreen(videoWrapperRef.current);
    } else {
      exitFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  };

  // Enter fullscreen
  const enterFullscreen = (element) => {
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    } else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen();
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen();
    }
  };

  // Exit fullscreen
  const exitFullscreen = () => {
    if (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    ) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  // Handle video end
  const handleVideoEnd = () => {
    if (!fields?.loop?.value) {
      exitFullscreen();
      setShowVideo(false);
    }
  };

  // Tooltip handler
  const handleTooltip = (text) => {
    setTooltipText(text);
  };

  const renderHeading = (field, type) => {
    if (!field) {
      return null;
    }
    switch (type) {
      case 'H1':
        return (
          <h1>
            <Text field={field} />
          </h1>
        );
      case 'H2':
        return (
          <h2>
            <Text field={field} />
          </h2>
        );
      case 'H3':
        return (
          <h3>
            <Text field={field} />
          </h3>
        );
      default:
        return (
          <div>
            <Text field={field} />
          </div>
        );
    }
  };

  // Render content (title, subtitle, description, and play button)
  const renderContent = () => (
    <div className={`content ${fields?.contentPosition?.fields?.value?.value}`}>
      {renderHeading(fields?.Title, fields?.titleType?.fields?.value?.value)}
      {renderHeading(fields?.SubTitle, fields?.subTitleType?.fields?.value?.value)}
      <p>
        <RichText field={fields?.Description} />
      </p>
      <div style={{ display: 'flex', gap: '10px' }}>
        {renderCtaLink({
          field: fields.CallToAction,
          tracking: trackingAttributes,
        })}
        <AdsCtaButton level="primary" theme={themeMode} onClick={handlePlayVideo}>
          {fields?.CallToActionTitle?.value}
        </AdsCtaButton>
      </div>
    </div>
  );

  // Render video controls
  const renderControls = () => (
    <div className="controls">
      <ControlButton
        onClick={handleStop}
        onMouseEnter={() => handleTooltip(fields?.stopText?.value)}
        onMouseLeave={() => handleTooltip('')}
        icon={<i className="fa-regular fa-circle-stop"></i>}
        tooltip={tooltipText === fields?.stopText?.value ? tooltipText : ''}
      />

      {fields?.enableVolume?.value && (
        <ControlButton
          onClick={handleMuteToggle}
          onMouseEnter={() =>
            handleTooltip(isMuted ? fields?.unmuteText?.value : fields?.muteText?.value)
          }
          onMouseLeave={() => handleTooltip('')}
          icon={
            isMuted ? (
              <i className="fa-solid fa-volume-xmark"></i>
            ) : (
              <i className="fa-solid fa-volume-high"></i>
            )
          } //mute unmute icons
          tooltip={
            tooltipText === (isMuted ? fields?.unmuteText?.value : fields?.muteText?.value)
              ? tooltipText
              : ''
          }
        />
      )}

      {fields?.enableFullScreen?.value && (
        <ControlButton
          onClick={handleFullscreen}
          onMouseEnter={() =>
            handleTooltip(isFullscreen ? fields?.compressText?.value : fields?.expandText?.value)
          }
          onMouseLeave={() => handleTooltip('')}
          icon={
            isFullscreen ? (
              <i className="fa-solid fa-compress"></i>
            ) : (
              <i className="fa-solid fa-expand"></i>
            )
          } // Fullscreen and exit Fullscreen icons
          tooltip={
            tooltipText === (isFullscreen ? fields?.compressText?.value : fields?.expandText?.value)
              ? tooltipText
              : ''
          }
        />
      )}
    </div>
  );

  // Reusable control button component
  const ControlButton = ({ onClick, onMouseEnter, onMouseLeave, icon, tooltip }) => (
    <div className="tooltip-container">
      <p onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {icon}
        {tooltip && <span className="tooltip top-tooltip">{tooltip}</span>}
      </p>
    </div>
  );

  if (fields?.enableIntrolVideo?.value && !introVideoPlayed && fields?.introVideoUrl?.value?.src) {
    return (
      <div className={`component-wrapper ${modifiedBackgroundColour}`}>
        <div
          className={`feature-advanced-video video-container ${
            showVideo ? 'video-active' : 'image-active'
          } ${fields?.isFullWidth.value ? 'full-width' : ''}`}
          style={{
            width: fields?.isFullWidth.value ? '100vw' : '1100px',
            height: height + 'px',
            paddingTop: paddingTop + 'px',
            paddingBottom: paddingBottom + 'px',
          }}
        >
          <div
            className="video-wrapper"
            style={{
              height: fields?.isFullWidth.value ? '100%' : videoWrapperHeight + 'px',
            }}
            ref={videoWrapperRef}
          >
            <video
              ref={videoRef}
              className={`custom-video ${fields?.isFullWidth.value ? 'full-width-video' : ''}`}
              onEnded={() => {
                handleVideoEnd();
                setIntroVideoPlayed(true); // Mark intro video as played
              }}
              muted={true}
              onCanPlayThrough={() => videoRef.current?.play()} // Play the video when it's ready
              autoPlay={true}
            >
              <source src={fields?.introVideoUrl?.value?.src} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>

          <div
            style={{
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              height: fields?.isFullWidth.value ? '100%' : videoWrapperHeight + 'px',
              width: '100%',
              backgroundImage: !showVideo ? `url(${fields?.Image?.value?.src})` : 'none',
              zIndex: '1',
            }}
          ></div>

          {/* Show Content on Intro Video*/}
          {fields?.isShowContentOnIntroVideo.value && renderContent()}
        </div>
      </div>
    );
  }

  return (
    <div className={`component-wrapper ${modifiedBackgroundColour}`}>
      <div
        className={`feature-advanced-video video-container ${
          showVideo ? 'video-active' : 'image-active'
        } ${fields?.isFullWidth.value ? 'full-width' : ''}`}
        style={{
          width: fields?.isFullWidth?.value ? '100vw' : '1100px',
          paddingTop: paddingTop + 'px',
          paddingBottom: paddingBottom + 'px',
        }}
      >
        {!showVideo && renderContent()}

        <div
          style={{
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            height: fields?.isFullWidth?.value ? '100%' : videoWrapperHeight + 'px',
            width: '100%',
            backgroundImage: !showVideo ? `url(${fields?.Image?.value?.src})` : 'none',
            zIndex: '1',
          }}
        ></div>
        {showVideo && (
          <div
            className="video-wrapper"
            style={{
              height: fields?.isFullWidth.value ? '100%' : videoWrapperHeight + 'px',
            }}
            ref={videoWrapperRef}
          >
            <video
              ref={videoRef}
              className={`custom-video ${fields?.isFullWidth?.value ? 'full-width-video' : ''}`}
              onEnded={handleVideoEnd}
              loop={fields?.loop?.value}
              muted={isMuted}
              onCanPlayThrough={() => videoRef.current?.play()}
              onLoadedMetadata={handleMetadataLoaded} // Get video dimensions here
              preload="auto" // Preload the video
              hidden={!showVideo} // Hide the video initially
            >
              <source src={fields?.videoUrl?.value?.src} type="video/mp4" />
              <track
                label={fields?.videoUrl?.value?.src}
                kind="subtitles"
                srcLang="en"
                src={fields?.WebVTTCaptionFilename?.value}
                default
              />
              Your browser does not support the video tag.
            </video>
            {renderControls()}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureAdvancedVideo;
